import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Spinner } from 'react-bootstrap';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';
import type { UnlockedAchievementPayload, UnlockedCharacterPayload, LoginStreakHome } from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';
import { achievementCircleColor } from '../utils/achievementRarity';
import { formatAchievementRarity } from '../utils/formatAchievementRarity';
import AchievementShareTemplate from './AchievementShareTemplate';
import { achievementIconSrcFromKey } from '../utils/achievementIconAsset';
import {
  captureAchievementShareCard,
  getQuizterSiteUrl,
  shareAchievementCardFromElement,
} from '../utils/shareAchievementImage';
import { playCelebrationTadaSfx } from '../audio/quizSfx';
import { quizQuestionImageUrlsFromKey, WITC_SILHOUETTE_FILTER_CSS } from '../utils/quizQuestionImageSrc';
import './AchievementCelebrationModal.css';
import './QuizFinishCelebrationModal.css';

function rarityColors(rarity: string): string[] {
  const r = rarity?.toLowerCase() || '';
  if (r.includes('epic')) return ['#a855f7', '#f59e0b', '#ec4899', '#fde047'];
  if (r.includes('rare')) return ['#3b82f6', '#06b6d4', '#60a5fa'];
  return ['#94a3b8', '#cbd5e1', '#64748b'];
}

const characterUnlockConfettiColors = ['#7c3aed', '#a78bfa', '#fbbf24', '#f472b6', '#38bdf8'];

function fireConfetti(colors: string[]) {
  confetti({
    particleCount: 100,
    spread: 72,
    origin: { y: 0.55 },
    colors,
    disableForReducedMotion: true,
  });
  setTimeout(() => {
    confetti({
      particleCount: 40,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors,
      disableForReducedMotion: true,
    });
    confetti({
      particleCount: 40,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors,
      disableForReducedMotion: true,
    });
  }, 200);
}

function tryPlaySound(soundKey?: string | null) {
  if (!soundKey?.trim()) return;
  const src = `${process.env.PUBLIC_URL || ''}/sounds/${encodeURIComponent(soundKey.trim())}.mp3`;
  const audio = new Audio(src);
  audio.volume = 0.5;
  audio.play().catch(() => {});
}

export type QuizFinishCelebrationSlide =
  | { kind: 'achievement'; data: UnlockedAchievementPayload }
  | { kind: 'character'; data: UnlockedCharacterPayload }
  | { kind: 'loginStreak'; data: LoginStreakHome };

function CharacterCelebrationImage({
  basePath,
  imageKey,
}: {
  basePath: string;
  imageKey: string | null | undefined;
}) {
  const urls = useMemo(() => quizQuestionImageUrlsFromKey(basePath, imageKey), [basePath, imageKey]);
  const [src, setSrc] = useState<string | null>(urls?.webp ?? urls?.png ?? null);
  const [broken, setBroken] = useState(false);
  const [reveal, setReveal] = useState(false);

  const reduceMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    const resolved = quizQuestionImageUrlsFromKey(basePath, imageKey);
    const u = resolved?.webp ?? resolved?.png ?? null;
    setSrc(u);
    setBroken(false);
    if (reduceMotion) {
      setReveal(true);
      return;
    }
    setReveal(false);
    const t = window.setTimeout(() => setReveal(true), 480);
    return () => clearTimeout(t);
  }, [basePath, imageKey, reduceMotion]);

  const showImg = urls && !broken;
  const effectiveSrc = src || urls?.png;

  return (
    <div className="character-celebration-frame">
      {showImg && effectiveSrc ? (
        <img
          src={effectiveSrc}
          alt=""
          decoding="async"
          className="character-celebration-img"
          style={{
            filter: reveal ? 'none' : WITC_SILHOUETTE_FILTER_CSS,
            transition: reduceMotion ? undefined : 'filter 1.35s ease-out',
          }}
          onError={() => {
            if (urls && src === urls.webp) {
              setSrc(urls.png);
            } else {
              setBroken(true);
            }
          }}
        />
      ) : (
        <span className="text-muted user-select-none" aria-hidden>
          —
        </span>
      )}
    </div>
  );
}

export interface QuizFinishCelebrationModalProps {
  show: boolean;
  achievements: UnlockedAchievementPayload[];
  characters: UnlockedCharacterPayload[];
  /** Shown as a celebration slide when the server recorded a new qualifying UTC day for login streak. */
  loginStreak?: LoginStreakHome | null;
  onFinished: () => void;
  playerNickname?: string;
}

const QuizFinishCelebrationModal: React.FC<QuizFinishCelebrationModalProps> = ({
  show,
  achievements,
  characters,
  loginStreak = null,
  onFinished,
  playerNickname = '',
}) => {
  const { t, ready, i18n } = useSafeTranslation();
  const [index, setIndex] = useState(0);
  const [sharing, setSharing] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const shareTemplateRef = useRef<HTMLDivElement>(null);

  const slides: QuizFinishCelebrationSlide[] = useMemo(() => {
    const s: QuizFinishCelebrationSlide[] = [];
    for (const a of achievements) {
      if (a) s.push({ kind: 'achievement', data: a });
    }
    for (const c of characters) {
      if (c && c.unlockableQuestionId) s.push({ kind: 'character', data: c });
    }
    if (loginStreak != null && typeof loginStreak.currentStreak === 'number') {
      s.push({ kind: 'loginStreak', data: loginStreak });
    }
    return s;
  }, [achievements, characters, loginStreak]);

  useEffect(() => {
    if (!show || slides.length === 0) return;
    const raf = requestAnimationFrame(() => {
      playCelebrationTadaSfx();
    });
    return () => cancelAnimationFrame(raf);
  }, [show, slides.length]);

  useEffect(() => {
    if (show) setIndex(0);
  }, [show, achievements, characters, loginStreak]);

  useEffect(() => {
    if (!show) {
      setPreviewBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [show]);

  const current = slides[index];

  const achievementCurrent = current?.kind === 'achievement' ? current.data : null;

  const rarityLabel = useMemo(
    () => (achievementCurrent ? formatAchievementRarity(achievementCurrent.rarity, t) : ''),
    [achievementCurrent, t]
  );

  const circleAriaLabel = useMemo(() => {
    if (!achievementCurrent) return '';
    if (ready) {
      return i18n.t('achievements.ariaBadge', {
        name: achievementCurrent.name,
        rarity: rarityLabel,
        points: achievementCurrent.points ?? 0,
      });
    }
    return `${achievementCurrent.name}, ${rarityLabel}, ${achievementCurrent.points ?? 0} points`;
  }, [achievementCurrent, ready, i18n, rarityLabel]);

  const celebrationIconSrc = useMemo(
    () => (achievementCurrent ? achievementIconSrcFromKey(achievementCurrent.iconAssetKey) : null),
    [achievementCurrent]
  );

  const shareHeadline = useMemo(() => {
    if (!achievementCurrent) return '';
    if (ready) {
      return i18n.t('achievements.shareHeadline', { name: achievementCurrent.name });
    }
    return `I unlocked the achievement: ${achievementCurrent.name}!`;
  }, [achievementCurrent, ready, i18n]);

  const shareCardNickname = useMemo(() => {
    const p = playerNickname.trim().replace(/^@+/, '');
    if (p) return p;
    return t('achievements.shareDefaultPlayer', 'Quizter Club player');
  }, [playerNickname, t]);

  const shareCardPlayerLine = useMemo(() => {
    if (!achievementCurrent) return '';
    return ready
      ? i18n.t('achievements.shareCardPlayerLine', { name: shareCardNickname })
      : `Player: ${shareCardNickname}`;
  }, [achievementCurrent, ready, i18n, shareCardNickname]);

  const shareCardRarityLine = useMemo(() => {
    if (!achievementCurrent) return '';
    return ready
      ? i18n.t('achievements.shareCardRarityLine', { rarity: rarityLabel })
      : `Rarity: ${rarityLabel}`;
  }, [achievementCurrent, ready, i18n, rarityLabel]);

  const shareCardUnlockedLine = useMemo(() => {
    if (!achievementCurrent?.unlockedAt) return '';
    const d = new Date(achievementCurrent.unlockedAt);
    if (Number.isNaN(d.getTime())) return '';
    const dateStr = new Intl.DateTimeFormat(i18n.language || 'en', {
      dateStyle: 'long',
    }).format(d);
    return ready ? i18n.t('achievements.shareCardUnlockedOn', { date: dateStr }) : `Unlocked on ${dateStr}`;
  }, [achievementCurrent, ready, i18n]);

  const runEffects = useCallback(() => {
    if (!show || !current) return;
    const reduceMotion =
      typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion) {
      if (current.kind === 'achievement') {
        fireConfetti(rarityColors(current.data.rarity));
        tryPlaySound(current.data.soundKey);
      } else if (current.kind === 'loginStreak') {
        fireConfetti(['#667eea', '#764ba2', '#a78bfa', '#93c5fd', '#34d399']);
      } else {
        fireConfetti(characterUnlockConfettiColors);
      }
    } else if (current.kind === 'achievement') {
      tryPlaySound(current.data.soundKey);
    }
  }, [show, current]);

  useEffect(() => {
    runEffects();
  }, [runEffects]);

  const handleClose = () => {
    onFinished();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') handleClose();
  };

  if (!show || slides.length === 0) return null;

  const modalTitle =
    slides.length > 1
      ? ready
        ? i18n.t('quiz.finishCelebrationProgressTitle', {
            current: index + 1,
            total: slides.length,
          })
        : `Unlocked (${index + 1} / ${slides.length})`
      : current.kind === 'achievement'
        ? ready
          ? i18n.t('achievements.celebrationSingleTitle')
          : 'Achievement unlocked!'
        : current.kind === 'character'
          ? ready
            ? i18n.t('quiz.characterUnlockCelebrationSingleTitle')
            : 'New character unlocked!'
          : ready
            ? i18n.t('quiz.loginStreakCelebrationSingleTitle', 'Streak updated!')
            : 'Streak updated!';

  const pointsLine =
    achievementCurrent && achievementCurrent.points != null
      ? ready
        ? i18n.t('achievements.celebrationPointsLine', {
            rarity: rarityLabel,
            points: achievementCurrent.points,
          })
        : `${rarityLabel} · +${achievementCurrent.points} pts`
      : achievementCurrent
        ? rarityLabel
        : '';

  const siteUrl = getQuizterSiteUrl();

  const previewImageAria = t('achievements.previewImageButton', 'Preview image');
  const shareButtonAria = t('achievements.shareButton', 'Share');
  const continueLabel = t('achievements.celebrationContinue', 'Continue');

  const handleShare = async () => {
    if (!achievementCurrent || !shareTemplateRef.current) return;
    setSharing(true);
    try {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      const title = t('achievements.shareDialogTitle', 'Quizter Club — Achievement');
      const text = `${shareHeadline}\n${siteUrl}`;
      const result = await shareAchievementCardFromElement(shareTemplateRef.current, {
        title,
        text,
        fileName: 'quizter-club-achievement.png',
      });
      if (result === 'shared') {
        toast.success(t('achievements.shareSuccess', 'Share sheet opened.'));
      } else if (result === 'downloaded') {
        toast.success(t('achievements.shareDownloaded', 'Image downloaded — you can share it from your files.'));
      }
    } catch {
      toast.error(t('achievements.shareError', 'Could not generate or share the image. Try again.'));
    } finally {
      setSharing(false);
    }
  };

  const handlePreviewImage = async () => {
    if (!achievementCurrent || !shareTemplateRef.current) return;
    setPreviewLoading(true);
    try {
      await new Promise<void>((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(() => resolve()))
      );
      const blob = await captureAchievementShareCard(shareTemplateRef.current);
      const url = URL.createObjectURL(blob);
      setPreviewBlobUrl((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return url;
      });
    } catch {
      toast.error(t('achievements.shareError', 'Could not generate or share the image. Try again.'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewBlobUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
  };

  const showShareControls = current.kind === 'achievement';
  const multi = slides.length > 1;

  const footerShareGroup = showShareControls ? (
    <>
      <Button
        variant="outline-secondary"
        className="achievement-celebration-icon-btn"
        onClick={() => void handlePreviewImage()}
        disabled={sharing || previewLoading}
        aria-busy={previewLoading}
        title={previewImageAria}
        aria-label={previewImageAria}
      >
        {previewLoading ? (
          <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-image" aria-hidden="true" />
        )}
      </Button>
      <Button
        variant="outline-primary"
        className="achievement-celebration-icon-btn"
        onClick={() => void handleShare()}
        disabled={sharing || previewLoading}
        aria-busy={sharing}
        title={shareButtonAria}
        aria-label={shareButtonAria}
      >
        {sharing ? (
          <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
        ) : (
          <i className="bi bi-share" aria-hidden="true" />
        )}
      </Button>
    </>
  ) : null;

  return (
    <>
      <Modal
        show={show}
        onHide={handleClose}
        centered
        backdrop="static"
        keyboard
        className="achievement-celebration-modal"
        backdropClassName="achievement-celebration-backdrop"
        dialogClassName="achievement-celebration-dialog"
        onKeyDown={handleKeyDown}
        aria-labelledby="quiz-finish-celebration-dialog-title"
      >
        <Modal.Header closeButton closeLabel={t('common.close', 'Close')}>
          <Modal.Title id="quiz-finish-celebration-dialog-title" className="w-100 text-center">
            {modalTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4" onKeyDown={handleKeyDown}>
          {current.kind === 'achievement' ? (
            <>
              <div
                className="achievement-celebration-circle mx-auto mb-3 d-flex align-items-center justify-content-center overflow-hidden"
                style={{ backgroundColor: achievementCircleColor(current.data.rarity) }}
                role="img"
                aria-label={circleAriaLabel}
              >
                {celebrationIconSrc ? (
                  <img src={celebrationIconSrc} alt="" className="achievement-celebration-circle-img" />
                ) : (
                  <i className="bi bi-trophy-fill achievement-celebration-fallback-icon" aria-hidden="true" />
                )}
              </div>
              <h3 className="mb-2" id="quiz-finish-celebration-name">
                {current.data.name}
              </h3>
              {current.data.description?.trim() ? (
                <p
                  className="achievement-celebration-description mb-3 px-1"
                  id="quiz-finish-celebration-desc"
                >
                  {current.data.description.trim()}
                </p>
              ) : null}
              <p className="achievement-celebration-rarity-line small mb-1">{pointsLine}</p>
            </>
          ) : current.kind === 'character' ? (
            <>
              <CharacterCelebrationImage
                key={current.data.unlockableQuestionId}
                basePath={(current.data.imageAssetsBasePath ?? '').trim()}
                imageKey={current.data.imageAssetKey}
              />
              <h3 className="mb-2 mt-2" id="quiz-finish-celebration-name">
                {t('quiz.characterUnlockCelebrationHeading', 'New character unlocked!')}
              </h3>
              <p className="character-celebration-subtitle mb-0 px-1">
                {t(
                  'quiz.characterUnlockCelebrationSubtitle',
                  'This character will now be available in this quiz. Have fun!'
                )}
              </p>
            </>
          ) : (
            <div className="qfc-login-streak">
              <div className="qfc-login-streak-icon-wrap" aria-hidden>
                <i className="bi bi-fire qfc-login-streak-flame" />
              </div>
              <div className="qfc-login-streak-count text-primary" aria-live="polite">
                {current.data.currentStreak}
              </div>
              <div className="qfc-login-streak-subtitle text-primary text-uppercase fw-bold">
                {t('loginStreak.dayStreakLabel', {
                  count: current.data.currentStreak,
                  defaultValue: 'Day streak',
                })}
              </div>
              <div className="qfc-login-streak-week rounded border p-2 mt-3 bg-light">
                <div className="qfc-login-streak-weekdays d-flex justify-content-between text-center small mb-2 px-1">
                  {(current.data.weekDays ?? []).map((d) => (
                    <span
                      key={d.dayOfWeek}
                      className={d.isToday ? 'text-primary fw-semibold' : 'text-muted'}
                    >
                      {t(`loginStreak.weekdayShort.${d.dayOfWeek}`, String(d.dayOfWeek))}
                    </span>
                  ))}
                </div>
                <div className="qfc-login-streak-dots d-flex justify-content-between px-1">
                  {(current.data.weekDays ?? []).map((d) => (
                    <span
                      key={`dot-${d.dayOfWeek}`}
                      className={`qfc-login-streak-dot ${d.completed ? 'qfc-login-streak-dot--done' : ''} ${
                        d.isToday ? 'qfc-login-streak-dot--today' : ''
                      }`}
                      aria-label={
                        d.completed
                          ? t('loginStreak.dayDoneAria', 'Completed')
                          : t('loginStreak.dayPendingAria', 'Not completed')
                      }
                    >
                      {d.completed ? <i className="bi bi-check-lg" aria-hidden /> : null}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between flex-wrap gap-2">
          {multi ? (
            <>
              <Button
                variant="outline-secondary"
                disabled={index === 0}
                onClick={() => setIndex((i) => Math.max(0, i - 1))}
              >
                {t('common.previous', 'Previous')}
              </Button>
              <div className="d-flex flex-wrap gap-2 ms-auto align-items-center">
                {footerShareGroup}
                {index < slides.length - 1 ? (
                  <Button variant="primary" onClick={() => setIndex((i) => Math.min(slides.length - 1, i + 1))}>
                    {t('common.next', 'Next')}
                  </Button>
                ) : (
                  <Button variant="primary" onClick={handleClose}>
                    {continueLabel}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <div className="d-flex flex-wrap gap-2 w-100 justify-content-end align-items-center">
              {footerShareGroup}
              <Button variant="primary" onClick={handleClose}>
                {continueLabel}
              </Button>
            </div>
          )}
        </Modal.Footer>
        {show && achievementCurrent ? (
          <AchievementShareTemplate
            ref={shareTemplateRef}
            playerLineText={shareCardPlayerLine}
            iconAssetKey={achievementCurrent.iconAssetKey}
            rarity={achievementCurrent.rarity}
            rarityLineText={shareCardRarityLine}
            siteUrl={siteUrl}
            headlineText={shareHeadline}
            descriptionText={achievementCurrent.description?.trim() ?? ''}
            unlockedDateLineText={shareCardUnlockedLine}
          />
        ) : null}
      </Modal>

      <Modal
        show={Boolean(previewBlobUrl)}
        onHide={closePreview}
        centered
        className="achievement-share-preview-modal"
        backdropClassName="achievement-share-preview-backdrop"
        dialogClassName="achievement-share-preview-dialog"
        contentClassName="achievement-share-preview-content"
        enforceFocus={false}
      >
        <Modal.Header closeButton closeLabel={t('common.close', 'Close')}>
          <Modal.Title id="achievement-share-preview-title">
            {t('achievements.previewImageTitle', 'Image preview')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="achievement-share-preview-body p-0">
          {previewBlobUrl ? (
            <div className="achievement-share-preview-frame">
              <img src={previewBlobUrl} alt="" className="achievement-share-preview-img" />
            </div>
          ) : null}
        </Modal.Body>
      </Modal>
    </>
  );
};

export default QuizFinishCelebrationModal;
